import { batch } from '@preact/signals-core';
import type * as Y from 'yjs';

const pending = new WeakMap<Y.Transaction, Map<unknown, () => void>>();

/**
 * Yjs calls the observers of one transaction one after another, and each
 * reactive mirror is updated inside its own observer. Notifying a listener from
 * there exposes sibling mirrors of the same transaction in their old state.
 * Notify every listener once, after all observers of the transaction ran.
 */
export function notifyAfterObservers(
  transaction: Y.Transaction,
  key: unknown,
  notify: () => void
): void {
  let queue = pending.get(transaction);
  if (!queue) {
    const notifications = new Map<unknown, () => void>();
    queue = notifications;
    pending.set(transaction, notifications);
    const { doc } = transaction;
    const flush = (done: Y.Transaction) => {
      if (done !== transaction) return;
      doc.off('afterTransaction', flush);
      pending.delete(transaction);
      const errors: unknown[] = [];
      batch(() => {
        for (const run of notifications.values()) {
          try {
            run();
          } catch (error) {
            errors.push(error);
          }
        }
      });
      // Like Yjs observers: every listener runs, the first failure surfaces.
      if (errors.length) throw errors[0];
    };
    doc.on('afterTransaction', flush);
  }
  queue.set(key, notify);
}
